"use client";
import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Typography } from "@/components/ui";
import { env } from "@/lib/environment";
import type { PricingCardData } from "@/lib/data/pricing-cards";
import { pricingCards } from "@/lib/data/pricing-cards";
import { BillingToggle } from "./BillingToggle";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";
import { useIsAdLanding } from "@/lib/hooks/useIsAdLanding";

const imgBaseUrl = `${env.NEXT_PUBLIC_CDN_URL}/images/pricing`;

interface PricingCardProps {
  data: PricingCardData;
  featured?: boolean;
  billingCycle: "monthly" | "annually";
  countryCode?: string;
}

export function PricingCard({
  data,
  featured = false,
  billingCycle,
  countryCode,
}: PricingCardProps) {
  const isCustomPricing = !!data.customPricing;
  // USD is the default so prices are in the server HTML; INR swaps in once geo resolves
  const isIndia = countryCode === "IN";
  const isAdLanding = useIsAdLanding();
  const basePricing = data.pricing?.[billingCycle];
  const currentPricing = isIndia && basePricing?.inr ? basePricing.inr : basePricing;

  const isConsoleRegisterCta = data.ctaButton.href.includes("console.pickcel.com");
  const ctaButton =
    isAdLanding && isConsoleRegisterCta
      ? { text: "Book a Demo", href: "/book-a-demo", variant: data.ctaButton.variant }
      : data.ctaButton;
  const isExternalCta = ctaButton.href.startsWith("http");

  return (
    <div
      className={`
        bg-white rounded-xl border border-[#efe7e1] overflow-clip
        shadow-[0_2px_24px_rgba(0,0,0,0.05)]
        flex flex-col h-full border-none
      `}
    >
      {/* Header Section */}
      <div className="border-b border-[#dfdfdf] bg-white/50 p-8 pb-9">
        <div className="flex flex-col gap-6">
          <Typography className="text-text-primary text-2xl font-semibold">{data.name}</Typography>

          {/* Container */}
          <div className="flex flex-col gap-5">
            {/* Subtitle */}
            <Typography className="text-text-primary text-base leading-5">
              {data.subtitle}
            </Typography>

            {/* Price Section */}
            <div className="relative min-h-[76px]">
              {isCustomPricing && data.customPricing ? (
                <div className="flex items-center gap-4 my-5">
                  <Image
                    src={`${imgBaseUrl}/chat.a3Kx9DYM5RRv.svg`}
                    alt="contact"
                    title="contact"
                    priority
                    fetchPriority="high"
                    width={38}
                    height={38}
                    className="flex-shrink-0"
                  />
                  <div>
                    <Typography className="text-text-primary text-xl font-semibold">
                      {data.customPricing.title}
                    </Typography>
                    <Typography as="p" className="text-text-primary text-sm">
                      {data.customPricing.description}
                    </Typography>
                  </div>
                </div>
              ) : currentPricing ? (
                <>
                  <div className="flex items-baseline gap-1.5">
                    <Typography className="text-text-primary text-[48px] font-semibold leading-[66px]">
                      {currentPricing.amount}
                    </Typography>
                    <Typography className="text-text-primary text-base">
                      {currentPricing.period}
                    </Typography>
                  </div>
                  <Typography className="text-text-primary text-sm mt-0">
                    {currentPricing.description}
                  </Typography>
                </>
              ) : null}
            </div>
          </div>

          {/* CTA Button — ad/UTM sessions swap console register for Book a Demo */}
          {isExternalCta ? (
            <Link
              href={ctaButton.href}
              target="_blank"
              rel="noopener noreferrer"
              data-mixpanel-id={isAdLanding && isConsoleRegisterCta ? "pricing-book-a-demo-utm" : undefined}
              className={`
                block w-full text-center py-3 px-6 rounded-lg text-base font-medium transition-colors
                ${
                  ctaButton.variant === "primary"
                    ? "bg-primary text-white hover:bg-primary/90 border-[1.5px] border-primary"
                    : "bg-white text-primary border-[1.5px] border-primary hover:bg-primary hover:text-white"
                }
              `}
            >
              {ctaButton.text}
            </Link>
          ) : (
            <Link
              href={ctaButton.href}
              data-mixpanel-id={isAdLanding && isConsoleRegisterCta ? "pricing-book-a-demo-utm" : undefined}
              className={`
                block w-full text-center py-3 px-6 rounded-lg text-base font-medium transition-colors
                ${
                  ctaButton.variant === "primary"
                    ? "bg-primary text-white hover:bg-primary/90 border-[1.5px] border-primary"
                    : "bg-white text-primary border-[1.5px] border-primary hover:bg-primary hover:text-white"
                }
              `}
            >
              {ctaButton.text}
            </Link>
          )}
        </div>
      </div>

      {/* Top Features */}
      <div className="px-6 pt-[28px] pb-[17px] flex flex-col gap-3">
        {data.topFeatures.map((feature, index) => (
          <div key={index} className="flex  items-center gap-3">
            <div className="w-8 h-8 bg-white border-4 border-white rounded-full flex items-center justify-center flex-shrink-0 mt-0">
              <Image
                src={`${imgBaseUrl}/tick-pricing-black.nwa_Dv3IWXNo.svg`}
                alt="check"
                width={16}
                height={16}
                priority
                fetchPriority="high"
                className="flex-shrink-0"
              />
            </div>
            <Typography className="text-[#191d23] text-sm leading-normal flex-1">
              {feature}
            </Typography>
          </div>
        ))}
      </div>

      {/* Additional Features */}
      {data.additionalFeatures && (
        <div className="px-6 pb-[17px] flex flex-col gap-[17px]">
          <Typography className="text-[#191d23] text-sm">
            {data.additionalFeatures.prefix}
          </Typography>
          <div className="flex flex-col gap-3">
            {data.additionalFeatures.features.map((feature, index) => (
              <div key={index} className="flex items-start gap-2.5">
                <div className="w-4 h-8 flex items-center justify-start flex-shrink-0">
                  <Image
                    src={`${imgBaseUrl}/tick-pricing-black.nwa_Dv3IWXNo.svg`}
                    alt="check"
                    title="check"
                    priority
                    fetchPriority="high"
                    width={16}
                    height={16}
                    className="flex-shrink-0"
                  />
                </div>
                <Typography className="text-[#191d23] text-base leading-[22px] flex-1">
                  {feature}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function PricingCards({ countryCodeOverride }: { countryCodeOverride?: string } = {}) {
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annually">("annually");
  const { geoData } = useGeoLocation();
  const effectiveCountryCode = countryCodeOverride ?? geoData?.countryCode;

  return (
    <div className="mt-10 w-full md:w-10/12 lg:w-11/12 mx-auto">
      {/* Country Display */}
      {/* <div className="text-center mb-6 min-h-[32px]">
        {geoData && !isLoading && (
          <Typography variant="h4" as="h4" className="text-gray-600">
            {geoData.countryName} ({geoData.countryCode})
          </Typography>
        )}
      </div> */}

      <div className="flex justify-center mb-12">
        <BillingToggle billingCycle={billingCycle} onBillingCycleChange={setBillingCycle} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-[1278px] mx-auto">
        {pricingCards.map(card => (
          <PricingCard
            key={card.id}
            data={card}
            featured={card.id === "business"}
            billingCycle={billingCycle}
            countryCode={effectiveCountryCode}
          />
        ))}
      </div>
    </div>
  );
}
